// WeekListSidebar.tsx
import React, { MouseEvent, useEffect, useRef, useState } from "react";

import moment, { Moment } from "moment";

import { ScheduleEventType } from "./sequence/ScheduleSetting";

export type WeekRange = {
  key: string;
  endDate: Moment;
  startDate: Moment;
  text: string;
  textWithYear: string;
  value: string;
};

type WeekListSidebarProps = {
  events?: ScheduleEventType[];
  filteredWeekRanges: WeekRange[];
  selectedWeeks: string[];
  onDragUpdate: (selectedWeeks: string[]) => void;
  onWeekSelect: (week: WeekRange, rangeDate: { end: Moment; start: Moment }) => void;
};

const WeekListSidebar = (props: WeekListSidebarProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartIndex, setDragStartIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedWeekRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);

  // Auto-scroll to selected week on first render
  useEffect(() => {
    if (!hasScrolledRef.current && selectedWeekRef.current && containerRef.current) {
      selectedWeekRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      hasScrolledRef.current = true;
    }
  }, [props.selectedWeeks]);

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        setDragStartIndex(null);
      }
    };

    document.addEventListener("mouseup", handleGlobalMouseUp);

    return () => document.removeEventListener("mouseup", handleGlobalMouseUp);
  }, [isDragging]);

  const handleWeekMouseDown = (week: WeekRange, index: number, event: MouseEvent) => {
    event.preventDefault();
    setIsDragging(true);
    setDragStartIndex(index);

    if (event.ctrlKey || event.metaKey) {
      if (props.selectedWeeks.includes(week.value)) {
        const updatedWeeks = props.selectedWeeks.filter((w) => w !== week.value);

        props.onDragUpdate(updatedWeeks);
      } else {
        const updatedWeeks = [...props.selectedWeeks, week.value];

        props.onDragUpdate(updatedWeeks);
      }
    } else if (event.shiftKey && props.selectedWeeks.length > 0) {
      const lastSelectedIndex = props.filteredWeekRanges.findIndex(
        (w) => w.value === props.selectedWeeks[0]
      );
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      const updatedSelection = props.filteredWeekRanges.slice(start, end + 1).map((w) => w.value);

      props.onDragUpdate(updatedSelection);
    } else {
      props.onDragUpdate([week.value]);
    }

    props.onWeekSelect(week, {
      end: week.endDate.clone(),
      start: week.startDate.clone(),
    });
  };

  const handleWeekMouseEnter = (week: WeekRange, index: number) => {
    if (!isDragging || dragStartIndex === null) {
      return;
    }

    const start = Math.min(dragStartIndex, index);
    const end = Math.max(dragStartIndex, index);
    const updatedSelection = props.filteredWeekRanges.slice(start, end + 1).map((w) => w.value);

    props.onDragUpdate(updatedSelection);
  };

  const handleWeekMouseUp = () => {
    setIsDragging(false);
    setDragStartIndex(null);
  };

  return (
    <div
      ref={containerRef}
      style={{
        overflowY: "auto",
        paddingRight: "8px",
        userSelect: "none",
        width: "145px",
      }}
    >
      {props.filteredWeekRanges.map((week, index) => {
        const isSelected = props.selectedWeeks.includes(week.value);
        const isFirstSelected = isSelected && week.value === props.selectedWeeks[0];

        const weekHasEvents = (props.events ?? []).some((event) => {
          const eventDate = moment(event.start);

          return (
            eventDate.isSameOrAfter(week.startDate, "day") &&
            eventDate.isSameOrBefore(week.endDate, "day")
          );
        });

        return (
          <div
            key={week.value}
            ref={isFirstSelected ? selectedWeekRef : null}
            style={{
              backgroundColor: isSelected ? "#FFD4DF" : "transparent",
              border: "1px solid #D9D9D9",
              borderBottomWidth: index === props.filteredWeekRanges.length - 1 ? "1px" : 0,
              borderRadius: "1px",
              color: weekHasEvents ? "rgb(229 75 117)" : "#3C3C3C",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: 400,
              padding: "5px 10px",
            }}
            onMouseDown={(e) => handleWeekMouseDown(week, index, e)}
            onMouseEnter={() => handleWeekMouseEnter(week, index)}
            onMouseUp={handleWeekMouseUp}
          >
            {week.text}
          </div>
        );
      })}
    </div>
  );
};

WeekListSidebar.displayName = "WeekListSidebar";

export default React.memo(WeekListSidebar);
